import type { ScanFrequency } from '@/sections/settings/subscription/constant';

interface MSRPInputs {
  numberOfApplications: number;
  scanSchedule: ScanFrequency | string;
  numberOfAssets?: number;
  selectedTier?: string;
  numberOfAEVExercises?: number;
  numberOfRedTeams?: number;
}

interface PricingBreakdown {
  fqdnCost: number;
  assetCost: number;
  aevCost: number;
  redTeamCost: number;
}

interface MSRPResult {
  floor: number;
  msrp: number;
  // True when the asset (platform) line is clamped to the $13K minimum.
  // floorAssetCost is 0 for empty configs, so this is false when no assets.
  atPlatformFloor: boolean;
  breakdown: PricingBreakdown; // MSRP-level breakdown
  floorBreakdown: PricingBreakdown; // Floor-level breakdown
  aevFloor: number;
  aevMsrp: number;
  redTeamFloor: number;
  redTeamMsrp: number;
}

// Floor prices (absolute minimum sales cannot go below)
const FQDN_FLOOR_RATE = 1000; // $1,000/application (DAST target)

const AEV_EXERCISE_FLOOR_RATE = 25000; // $25K floor / $50K MSRP per adversary emulation exercise

const RED_TEAM_RATE = 100000; // $100K per red team (ludacris only) — flat (no MSRP multiplier)

// MSRP multiplier (2× floor) — used by FQDN and AEV pricing only.
const MSRP_MULTIPLIER = 2;

// Asset pricing (compute-based, with a margin-protected floor)
// Compute cost basis: $1/asset/year at daily cadence; scales down by interval_days:
//   cost(annual) = round(numberOfAssets × 1 ÷ interval_days)
// Two guardrails on the asset-coverage line, take the higher (max):
//   • 80% gross-margin floor = 5 × cost   (sell-gate: never sell below 80% GM)
//   • $13K platform minimum  = ASSET_SCAN_MINIMUM (protects small accounts)
// MSRP (list) = 10 × cost → 90% GM, also never below $13K. Reps discount from
// MSRP toward the floor and GM stays ≥ 80% the whole way.
// FQDN (Application Scanning / DAST) and AEV keep their own pricing and stack on top.
const ASSET_COMPUTE_ANNUAL_RATE = 1; // $1/asset/year of compute at daily cadence
const ASSET_FLOOR_MULTIPLIER = 5; // 5× cost → 80% gross-margin floor
const ASSET_MSRP_MULTIPLIER = 10; // 10× cost → 90% gross-margin list (MSRP)
const ASSET_SCAN_MINIMUM = 13000; // $13K/yr absolute minimum (small accounts)

// Scan interval in days per cadence (denominator for the annual asset floor).
const INTERVAL_DAYS: Record<string, number> = {
  daily: 1,
  weekly: 7,
  biweekly: 14,
  monthly: 30,
  quarterly: 90,
};

// Frequency multipliers applied to FQDN pricing ONLY.
// These are intentionally legacy/out-of-scope for the asset-pricing change —
// FQDN scaling keeps its existing per-cadence multipliers and MSRP_MULTIPLIER = 2.
// Weekly scans take 2 days to finish, so floor = 2/7 of daily (0.2857) and MSRP = 0.57 of daily
// Bi-weekly = 2/14 of daily (0.1429); msrp = weekly msrp ÷ 2 (0.285)
// Monthly = weekly/4 (1 scan per month): floor = 0.0714, msrp = 0.1425
// Quarterly = weekly/12 (1 scan per quarter): floor = 0.0238, msrp = 0.0475
const FREQUENCY_MULTIPLIERS: Record<string, { floor: number; msrp: number }> = {
  daily: { floor: 1.0, msrp: 1.0 },
  weekly: { floor: 0.2857, msrp: 0.57 },
  biweekly: { floor: 0.1429, msrp: 0.285 },
  monthly: { floor: 0.0714, msrp: 0.1425 },
  quarterly: { floor: 0.0238, msrp: 0.0475 },
};

// Shared, pure floor-rule check — single source of truth used by both
// useMSRPCalculator and the Add Customer wizard validators so the "$13K
// platform floor" rule cannot drift between surfaces.
export function isAtPlatformFloor(inputs: {
  numberOfAssets?: number;
  scanSchedule: ScanFrequency | string;
}): boolean {
  const numberOfAssets = inputs.numberOfAssets || 0;
  if (numberOfAssets <= 0) return false;
  const intervalDays = INTERVAL_DAYS[inputs.scanSchedule] ?? 1;
  const assetComputeCost = Math.round(
    (numberOfAssets * ASSET_COMPUTE_ANNUAL_RATE) / intervalDays
  );
  const floorAssetCost = Math.max(
    assetComputeCost * ASSET_FLOOR_MULTIPLIER,
    ASSET_SCAN_MINIMUM
  );
  return floorAssetCost === ASSET_SCAN_MINIMUM;
}

// Note: This is a pure calculation function despite the `use` prefix.
// The useMemo was removed (React 19 Compiler handles memoization).
// Keeping the hook name for import stability across the codebase.
export function useMSRPCalculator(inputs: MSRPInputs): MSRPResult {
  // FQDN frequency multiplier (legacy/out-of-scope). Assets no longer use this.
  const multiplier = FREQUENCY_MULTIPLIERS[inputs.scanSchedule] || {
    floor: 1,
    msrp: 1,
  };

  const numberOfAssets = inputs.numberOfAssets || 0;
  const numberOfApplications = inputs.numberOfApplications || 0;

  const intervalDays = INTERVAL_DAYS[inputs.scanSchedule] ?? 1;
  const assetComputeCost = Math.round(
    (numberOfAssets * ASSET_COMPUTE_ANNUAL_RATE) / intervalDays
  );
  // Floor: 80% gross-margin point (5× cost), never below the $13K minimum. Guard on
  // numberOfAssets > 0 (not raw cost) so a tiny count still hits $13K; 0 assets → $0.
  const floorAssetCost =
    numberOfAssets > 0
      ? Math.max(assetComputeCost * ASSET_FLOOR_MULTIPLIER, ASSET_SCAN_MINIMUM)
      : 0;
  // True when the platform line is clamped to the minimum floor. Naturally
  // false when numberOfAssets === 0 (floorAssetCost is 0 there).
  const atPlatformFloor = isAtPlatformFloor({
    numberOfAssets,
    scanSchedule: inputs.scanSchedule,
  });
  // MSRP: 90% gross-margin list (10× cost), never below the $13K minimum.
  const msrpAssetCost =
    numberOfAssets > 0
      ? Math.max(assetComputeCost * ASSET_MSRP_MULTIPLIER, ASSET_SCAN_MINIMUM)
      : 0;

  // FQDN floor: flat rate × count × floor frequency multiplier (UNCHANGED)
  const floorFqdnCost = Math.round(
    numberOfApplications * FQDN_FLOOR_RATE * multiplier.floor
  );

  // AEV per-exercise pricing (Ultimate-class tiers only).
  // ludacris mirrors Ultimate pricing until product specifies otherwise.
  const isUltimate =
    inputs.selectedTier === 'ultimate' || inputs.selectedTier === 'ludacris';
  const numberOfAEVExercises = isUltimate
    ? inputs.numberOfAEVExercises || 0
    : 0;
  const floorAEVCost = Math.round(
    numberOfAEVExercises * AEV_EXERCISE_FLOOR_RATE
  );
  const msrpAEVCost = Math.round(
    numberOfAEVExercises * AEV_EXERCISE_FLOOR_RATE * MSRP_MULTIPLIER
  );

  // Red team pricing (ludacris-only). Flat rate — the same value applies to
  // BOTH floor and MSRP (no 2× spread).
  const isLudacris = inputs.selectedTier === 'ludacris';
  const numberOfRedTeams = isLudacris ? inputs.numberOfRedTeams || 0 : 0;
  const redTeamCost = Math.round(numberOfRedTeams * RED_TEAM_RATE);

  const floor = floorAssetCost + floorFqdnCost + floorAEVCost + redTeamCost;

  // FQDN MSRP: flat rate × MSRP_MULTIPLIER × MSRP frequency multiplier (UNCHANGED)
  const msrpFqdnCost = Math.round(
    numberOfApplications * FQDN_FLOOR_RATE * MSRP_MULTIPLIER * multiplier.msrp
  );
  const msrp = msrpAssetCost + msrpFqdnCost + msrpAEVCost + redTeamCost;

  return {
    floor,
    msrp,
    atPlatformFloor,
    breakdown: {
      fqdnCost: msrpFqdnCost,
      assetCost: msrpAssetCost,
      aevCost: msrpAEVCost,
      redTeamCost,
    },
    floorBreakdown: {
      fqdnCost: floorFqdnCost,
      assetCost: floorAssetCost,
      aevCost: floorAEVCost,
      redTeamCost,
    },
    aevFloor: floorAEVCost,
    aevMsrp: msrpAEVCost,
    redTeamFloor: redTeamCost,
    redTeamMsrp: redTeamCost,
  };
}

// Helper function to format currency
export function formatMSRP(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}
